// 판매점이 직접 등록하는 정보 — 카탈로그 SSOT.
//
// 같은 카탈로그가 두 화면에서 쓰인다:
//   1) RetailerInfoEditModal — 입력 모달의 탭·그룹·입력 컨트롤 구성
//   2) FormTemplateDetailPage — "매장 상수" 팔레트 항목 목록
//
// 라벨이 곧 키 역할을 하는(=tenant.formDefaults.<라벨>) 팔레트 규약을 따른다.
// 팔레트엔 이미 옛 키들이 일부 들어있어 서식 템플릿이 거기에 매핑돼 있을 수
// 있으므로 기존 라벨은 이 SSOT 에서도 동일 라벨로 맞춰 중복 노출을 막는다.

import { WIRELESS_AUTOFILL_FIELD_META } from "./wireless-consult-autofill.js";

/**
 * "중고/유심단독 개통"(usedSimActivation) 값 정규화.
 *
 * 과거엔 고정 체크박스(다중 선택)라 값이 `string[]`(예: ["유심 단독"])로 저장됐고,
 * 지금은 단일 선택 드롭다운이라 `string`("유심 단독")로 저장된다. 이미 쌓인 옛 신청서가
 * 배열로 들어 있을 수 있으므로, 읽을 때 항상 단일 문자열로 환산한다(배열이면 첫 값).
 * 빈 값/없음이면 "".
 */
export function normalizeUsedSimActivation(value: unknown): string {
  if (Array.isArray(value)) return (value[0] as string) ?? "";
  if (typeof value === "string") return value;
  return "";
}

/**
 * 개통대리점 이름 정규화 — 영문은 대문자로 통일하고 앞뒤 공백을 떼어,
 * "ps"·"PS"·" ps " 가 같은 한 값("PS")으로 모이게 한다. 한글·숫자는 영향받지 않는다.
 * 프론트(DealerCombobox 의 새로 추가·이름수정)와 백엔드(마스터 저장·신청서 판매점정보
 * 저장)가 모두 이 함수 하나를 호출해 같은 규칙을 쓰므로, 어느 통로로 들어와도
 * 소문자가 DB 에 남지 않는다.
 */
export function normalizeDealer(value: unknown): string {
  return String(value ?? "").trim().toUpperCase();
}

export type RetailerFieldKind =
  | "text"
  | "number"
  | "date"
  | "time"
  | "datetime"
  | "textarea"
  | "select"
  | "radio"
  | "checkbox"
  | "file"
  | "calc";

/**
 * kind: "calc" 필드에 붙이는 계산 함수.
 * values — 현재 모달/카드의 전체 입력값 맵.
 * 반환값이 number 이면 호출 측에서 Intl.NumberFormat("ko-KR") 으로 포맷한다.
 * 유효한 입력이 전혀 없으면 "—" 를 반환해 "입력 없음"을 표시한다.
 */
export type RetailerFieldCompute = (
  values: Record<string, unknown>,
) => number | "—";

export interface RetailerField {
  /** UI key — 모달 내부 state 식별자. */
  key: string;
  /** 사용자에게 보이는 라벨이자 팔레트에서의 tenant.formDefaults.<label> 키. */
  label: string;
  kind: RetailerFieldKind;
  hint?: string;
  options?: string[];
  /** 값은 시스템이 채우고 화면에서는 확인만 하는 입력칸. */
  readOnly?: boolean;
  /**
   * select 옵션을 매장 마스터(/crm/master/:kind)에서 — 커스텀 항목(field-registry)
   * 전용. 지정되면 options 대신 MasterCombobox(검색+추가형)로 렌더한다.
   */
  optionsMaster?: string;
  /** select 다중 선택 (커스텀 항목 전용). 값은 쉼표 연결 문자열로 저장. */
  multiple?: boolean;
  /** 커스텀 카탈로그 항목이 신청서에서 사용할 검색 원본. 현재 plans만 선택형으로 지원한다. */
  catalog?: "models" | "plans";
  /** ⚙ 관리 버튼 노출 (마스터 데이터 모달 예정). */
  manage?: boolean;
  /** 모달 그리드에서 한 줄 전체 폭으로 표시. */
  full?: boolean;
  /**
   * 전화번호 칸이라 우클릭하면 **매장폰 문자·전화 메뉴**가 뜬다(티켓 #523).
   * 개통 고객 번호가 아니므로 '고객정보 3종 간편복사'는 붙지 않는다.
   * 짝이 되는 이름 칸이 `<key>Name`(예: referrerPhone ↔ referrerName)이면 문자 창 제목에 함께 쓴다.
   */
  quickContact?: boolean;
  /** true 면 팔레트에서 제외 (모달 전용 자동계산·UI 보조 항목 등). */
  paletteHidden?: boolean;
  /**
   * kind: "calc" 필드에만 설정. 다른 필드 값으로부터 결과를 실시간 계산한다.
   * 모달·카드 양쪽에서 동일하게 사용하므로 식은 여기 SSOT 한 곳에만 둔다.
   */
  compute?: RetailerFieldCompute;
  /**
   * 커스텀 항목 전용 (task 282) — 신청서 화면(applicationView)의 표시 덮어쓰기.
   * customFieldToRetailerField 가 CustomFieldDef.display 에서 옮겨 담는다.
   */
  displayOverride?: import("./field-consumers.js").FieldDisplayOverride;
}

export interface RetailerFieldGroup {
  title: string;
  fields: RetailerField[];
}

export interface RetailerFieldTab {
  title: string;
  groups: RetailerFieldGroup[];
}

/**
 * 예전 신청서의 통합 일시를 날짜/시간 필드로 나눈다.
 *
 * 접수일·개통일은 이제 날짜만 저장하고, 시간은 각각 receivedTime·activatedTime에
 * 저장한다. 날짜+시간이 한 문자열로 남아 있는 과거 jsonb는 읽거나 저장할 때 이
 * 함수로 정리한다. 형식을 알 수 없는 옛 값은 손대지 않아 데이터 손실을 피한다.
 */
export function normalizeRetailerDateTimes(
  values: Record<string, unknown>,
): Record<string, unknown> {
  const next = { ...values };
  for (const [dateKey, timeKey] of [
    ["receivedAt", "receivedTime"],
    ["activatedAt", "activatedTime"],
  ] as const) {
    const raw = next[dateKey];
    if (typeof raw !== "string") continue;
    const match = raw
      .trim()
      .match(/^(\d{4}-\d{2}-\d{2})(?:[T\s](\d{2}:\d{2})(?::\d{2})?)?$/);
    if (!match) continue;
    next[dateKey] = match[1];
    if ((next[timeKey] == null || next[timeKey] === "") && match[2]) {
      next[timeKey] = match[2];
    }
  }
  return next;
}

// ─────────────────────────────────────────────────────────────────────────────
// 자동계산 헬퍼
// ─────────────────────────────────────────────────────────────────────────────

/** 입력값을 숫자로 변환. 빈 문자열·null·undefined·NaN → 0 으로 처리. */
function toNum(v: unknown): number {
  if (v == null || v === "") return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/** 사전예약 혜택금과 할인 전 출고가를 retailer_info 에 저장하는 정식 키. */
export const PREORDER_BENEFIT_AMOUNT_KEY = "preorderBenefitAmount";
export const PREORDER_BENEFIT_ORIGINAL_RELEASE_PRICE_KEY =
  "preorderBenefitOriginalReleasePrice";

/**
 * 사전예약 혜택금이 있는 신청서의 출고가를 일관되게 계산한다.
 *
 * - 혜택금을 처음 적으면 현재 출고가를 할인 전 금액으로 보관한다.
 * - 혜택금을 고치면 보관한 할인 전 금액에서 다시 계산한다.
 * - 혜택금을 지우면 할인 전 금액을 복구하고 내부 보관값도 비운다.
 * - 출고가를 직접/모델 선택으로 새로 넣으면 그 값을 새 할인 전 금액으로 삼는다.
 * - 혜택금이 출고가보다 큰 비정상 입력은 화면·서식에 음수가 나가지 않게 0원으로 막는다.
 */
export function applyPreorderBenefitPricing(
  values: Record<string, unknown>,
  change:
    | { kind: "benefit"; value: unknown }
    | { kind: "basePrice"; value: unknown },
): Record<string, unknown> {
  const next = { ...values };
  if (change.kind === "benefit") {
    const rawBenefit = change.value;
    const benefit = Math.max(0, toNum(rawBenefit));
    const storedBase = next[PREORDER_BENEFIT_ORIGINAL_RELEASE_PRICE_KEY];
    const base =
      storedBase == null || storedBase === ""
        ? toNum(next.releasePrice)
        : toNum(storedBase);

    next[PREORDER_BENEFIT_AMOUNT_KEY] = rawBenefit;
    if (rawBenefit == null || rawBenefit === "" || benefit === 0) {
      if (storedBase != null && storedBase !== "") {
        next.releasePrice = String(base);
      }
      delete next[PREORDER_BENEFIT_ORIGINAL_RELEASE_PRICE_KEY];
      return next;
    }

    next[PREORDER_BENEFIT_ORIGINAL_RELEASE_PRICE_KEY] = String(base);
    next.releasePrice = String(Math.max(0, base - benefit));
    return next;
  }

  const rawBase = change.value;
  const benefit = Math.max(0, toNum(next[PREORDER_BENEFIT_AMOUNT_KEY]));
  if (benefit === 0) {
    next.releasePrice = rawBase;
    delete next[PREORDER_BENEFIT_ORIGINAL_RELEASE_PRICE_KEY];
    return next;
  }

  const base = Math.max(0, toNum(rawBase));
  next[PREORDER_BENEFIT_ORIGINAL_RELEASE_PRICE_KEY] = String(base);
  next.releasePrice = String(Math.max(0, base - benefit));
  return next;
}

/** 지정한 키들 중 유효한 값(null/빈문자열 아닌)이 하나라도 있는지 확인. */
function hasAny(values: Record<string, unknown>, keys: string[]): boolean {
  return keys.some((k) => values[k] != null && values[k] !== "");
}

// ─────────────────────────────────────────────────────────────────────────────
// 계산 함수
// ─────────────────────────────────────────────────────────────────────────────

const computeRebateSum: RetailerFieldCompute = (values) => {
  const deps = [
    "rebateBase",
    "verbal1",
    "verbal2",
    "verbal3",
    "verbal4",
    "verbal5",
    "verbal6",
    "verbal7",
    "verbal8",
    "verbal9",
    "verbal10",
  ];
  if (!hasAny(values, deps)) return "—";
  return (
    toNum(values.rebateBase) +
    toNum(values.verbal1) +
    toNum(values.verbal2) +
    toNum(values.verbal3) +
    toNum(values.verbal4) +
    toNum(values.verbal5) +
    toNum(values.verbal6) +
    toNum(values.verbal7) +
    toNum(values.verbal8) +
    toNum(values.verbal9) +
    toNum(values.verbal10)
  );
};

/**
 * 차감 합계 — 매장이 단일 숫자로 직접 입력한 deductionInput 을 그대로 사용.
 */
const computeDeductionSum: RetailerFieldCompute = (values) => {
  if (!hasAny(values, ["deductionInput"])) return "—";
  return toNum(values.deductionInput);
};

/**
 * 차감금액 합산 — deductionSum 과 동일한 값 (중복 표기).
 * 두 라벨 모두 같은 입력원(deductionInput)을 참조한다.
 */
const computeDeductionTotal: RetailerFieldCompute = (values) => {
  if (!hasAny(values, ["deductionInput"])) return "—";
  return toNum(values.deductionInput);
};

export const SIM_PURCHASE_TYPES = [
  "후납(다음달 요금합산)",
  "선납",
  "무상유심(대리점 제공)",
  "기존유심",
  "이심(가상유심)",
] as const;

export const LEGACY_SIM_PURCHASE_TYPE = "매장구매(고객에게 제공)";
export const SIM_USAGE_LOSS_FIELD_LABEL = "유심사용손해";

type SimAmounts = {
  customerCharge: string;
  dealerSettlement: string;
};

/** 구매방식 하나가 고객 청구·대리점 정산 두 금액을 함께 결정한다. */
export function simAmountsForPurchaseType(value: unknown): SimAmounts | null {
  switch (value) {
    case "후납(다음달 요금합산)":
      return { customerCharge: "7700", dealerSettlement: "7700" };
    case "선납":
      return { customerCharge: "0", dealerSettlement: "0" };
    case "무상유심(대리점 제공)":
      return { customerCharge: "7700", dealerSettlement: "0" };
    case "기존유심":
      return { customerCharge: "0", dealerSettlement: "0" };
    case "이심(가상유심)":
      return { customerCharge: "2750", dealerSettlement: "0" };
    default:
      return null;
  }
}

/** 매장이 사 둔 유심이 소멸되는 구매방식에서만 손해를 잡는다. */
export function simUsageLossForPurchaseType(value: unknown): string {
  return value === "후납(다음달 요금합산)" || value === "선납"
    ? "7700"
    : "0";
}

/**
 * 유심 구매방식에서 파생한 두 값을 저장 형태로 맞춘다.
 * 옛 simPrice와 옛 "매장구매" 선택값도 이 경계에서 한 번에 제거한다.
 */
export function applySimPurchaseTypeValues(
  values: Record<string, unknown>,
  simUsageLossKeys: readonly string[] = [],
): Record<string, unknown> {
  const next = { ...values };
  if (next.simPurchaseType === LEGACY_SIM_PURCHASE_TYPE) {
    next.simPurchaseType = "무상유심(대리점 제공)";
  }
  delete next.simPrice;

  const amounts = simAmountsForPurchaseType(next.simPurchaseType);
  if (amounts) {
    next.simCustomerChargePrice = amounts.customerCharge;
    next.simDealerSettlementPrice = amounts.dealerSettlement;
  } else {
    delete next.simCustomerChargePrice;
    delete next.simDealerSettlementPrice;
  }
  for (const key of simUsageLossKeys) {
    next[key] = simUsageLossForPurchaseType(next.simPurchaseType);
  }
  return next;
}

const computeRebateActual: RetailerFieldCompute = (values) => {
  const rebateSum = computeRebateSum(values);
  if (rebateSum === "—") return "—";
  const deductionSum = computeDeductionSum(values);
  return rebateSum - (deductionSum === "—" ? 0 : deductionSum);
};

/**
 * 개통시 할부금액 = 출고가 − 공시지원금 − 추가지원금 − 고객수납금(프리할부).
 * 고객이 할부로 갚아 나갈 원금에 해당한다. 네 항목이 모두 비어 있으면 "—".
 *
 * 이 값은 구매정보의 할부원금(installmentPrincipal) 칸에도 자동으로 흘러들어가
 * 채워진다(RetailerInfoEditModal 의 자동채움 effect). 직원이 할부원금을 손으로
 * 고치면 그 값이 우선되며, 저장·출력(PDF·대리점 문자)에는 할부원금 칸 값이 쓰인다
 * — 이 calc 칸 자체는 화면 표시용이라 저장되지 않기 때문이다.
 *
 * 이력: 2026-06-02 출고가식 신설(b35d596) → 06-07 할부원금 미러링(4c4f1bf)
 *       → 06-07 출고가식 복원 + 할부원금 자동연동.
 */
export const computeActivationInstallment: RetailerFieldCompute = (values) => {
  const deps = [
    "releasePrice",
    "publicSubsidy",
    "extraSubsidy",
    "freeInstallmentPayment",
  ];
  if (!hasAny(values, deps)) return "—";
  return (
    toNum(values.releasePrice) -
    toNum(values.publicSubsidy) -
    toNum(values.extraSubsidy) -
    toNum(values.freeInstallmentPayment)
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// 정산금·마진 단일 계산 소스
// ─────────────────────────────────────────────────────────────────────────────
//
// 정산금·마진·"계산내역" 풀이가 서로 어긋나지 않도록, 계산 항목을 한 곳에서
// 배열로 만들고 그 합으로 정산금/마진을 도출한다. 화면의 풀이 위젯도 이 배열을
// 그대로 받아 그리므로, 풀이에 적힌 합과 정산금/마진 숫자가 구조적으로 항상 일치한다.

/** 정산 계산식의 한 항목. */
export interface SettlementTerm {
  label: string;
  op: "+" | "-";
  /** 표시용 금액(양수 크기). */
  value: number;
  /** 실제 합산 기여 = op === "-" ? -value : value. */
  signed: number;
}

export interface SettlementResult {
  /** 대리점 정산금 = settlementTerms 의 signed 합. */
  settlementTerms: SettlementTerm[];
  settlement: number | "—";
  /** 정산금 소계 이후 항(중고폰 수입금 +, 기타 지원금 −). */
  marginTerms: SettlementTerm[];
  margin: number | "—";
}

function makeTerm(label: string, op: "+" | "-", value: number): SettlementTerm {
  return { label, op, value, signed: op === "-" ? -value : value };
}

// ─────────────────────────────────────────────────────────────────────────────
// 정산 공식의 뼈대 (SSOT) — 항목 순서·부호·참조 대상
// ─────────────────────────────────────────────────────────────────────────────
//
// "어떤 항목이 / 어떤 순서로 / 어떤 부호로 / 무엇을 참조하는가"만 여기 한 곳에
// 둔다. 고정식(computeSettlement)과 기본 커스텀식 시드(settlement-config.ts 의
// DEFAULT_SETTLEMENT_CONFIG)가 둘 다 이 배열에서 파생되므로, 정산 항목을
// 추가·삭제·재정렬·부호변경할 땐 여기만 고치면 두 경로가 함께 따라온다.
//
// 라벨·빈값 표시규칙은 공식이 아니라 표시 정책이라 여기 두지 않는다 — 고정식은
// 아래 label 을, 커스텀식 평가기는 source 에서 유도한 라벨을 각자 쓴다.

/** 정산 공식 항목이 값을 끌어오는 곳. settlement-config 의 TermSource 와 호환. */
export type SettlementSource =
  | { kind: "field"; key: string } // 정산영역 숫자 입력칸
  | { kind: "computed"; id: "rebateSum" | "deductionSum" | "simNapAdjust" }; // 특수 수량

export interface SettlementFormulaTerm {
  /** 고정식 "계산내역" 에 찍히는 라벨. */
  label: string;
  op: "+" | "-";
  source: SettlementSource;
}

/** 정산금 = 리베이트합계 − 차감합산 − 추가지원금 − 고객수납금(프리할부) − 번호이동수수료 + 유심 정산반영. */
export const SETTLEMENT_FORMULA_TERMS: SettlementFormulaTerm[] = [
  { label: "리베이트합계", op: "+", source: { kind: "computed", id: "rebateSum" } },
  { label: "차감합산", op: "-", source: { kind: "computed", id: "deductionSum" } },
  { label: "추가지원금", op: "-", source: { kind: "field", key: "extraSubsidy" } },
  { label: "고객수납금(프리할부)", op: "-", source: { kind: "field", key: "freeInstallmentPayment" } },
  { label: "번이 수수료", op: "-", source: { kind: "field", key: "mnpFee" } },
  { label: "유심 정산반영", op: "+", source: { kind: "computed", id: "simNapAdjust" } },
];

/** 마진 = 정산금 + 중고폰 수입금 − 기타 지원금. (정산금 소계 이후의 항만) */
export const MARGIN_FORMULA_TERMS: SettlementFormulaTerm[] = [
  { label: "중고폰 수입금", op: "+", source: { kind: "field", key: "usedPhonePurchase" } },
  { label: "기타 지원금", op: "-", source: { kind: "field", key: "otherSubsidy" } },
];

/**
 * 정산금·마진과 그 계산 항목을 한 번에 계산한다.
 *
 * 정산금 = 리베이트합계 − 차감합산 − 추가지원금 − 고객수납금(프리할부) − 번호이동수수료 + 유심 정산반영
 * 마진   = 정산금 + 중고폰 수입금 − 기타 지원금
 *
 * 항목 구성은 SETTLEMENT_FORMULA_TERMS / MARGIN_FORMULA_TERMS(공식 뼈대 SSOT)를
 * 따른다. '수납' 항은 직원 입력 freeInstallmentPayment 를 쓴다(고객 입력 actualPrice 는
 * 참고값). 유심은 구매방식이 채운 대리점정산 유심금액을 그대로 반영한다. computeDealerSettlement /
 * computeMargin 이 이 함수를 통해 값을 도출하므로 항목 합과 최종 숫자는 항상 일치한다.
 */
export function computeSettlement(
  values: Record<string, unknown>,
): SettlementResult {
  const rebateSumRaw = computeRebateSum(values);
  const rebateActualRaw = computeRebateActual(values);
  // 리베이트가 비어 rebateActual 이 "—"이면 기존 로직은 차감도 무시(base 0)했다.
  // 두 표시 항의 합이 항상 그 base 와 같도록 맞춰 동작을 보존한다.
  const rebateEmpty = rebateSumRaw === "—";

  // 공식 항목의 표시 금액(양수 크기)을 source 종류별로 뽑는다.
  const rawOf = (source: SettlementSource): number => {
    if (source.kind === "field") return toNum(values[source.key]);
    switch (source.id) {
      case "rebateSum":
        return rebateEmpty ? 0 : (rebateSumRaw as number);
      case "deductionSum":
        return rebateEmpty ? 0 : toNum(values.deductionInput);
      case "simNapAdjust":
        return toNum(values.simDealerSettlementPrice);
      default:
        return 0;
    }
  };

  const settlementTerms: SettlementTerm[] = SETTLEMENT_FORMULA_TERMS.map((t) =>
    makeTerm(t.label, t.op, rawOf(t.source)),
  );

  const settlementDeps = [
    "extraSubsidy",
    "freeInstallmentPayment",
    "mnpFee",
    "simDealerSettlementPrice",
    "deductionInput",
  ];
  const hasSettlementInput =
    rebateActualRaw !== "—" || hasAny(values, settlementDeps);
  const settlement: number | "—" = hasSettlementInput
    ? settlementTerms.reduce((sum, t) => sum + t.signed, 0)
    : "—";

  const marginTerms: SettlementTerm[] = MARGIN_FORMULA_TERMS.map((t) =>
    makeTerm(t.label, t.op, rawOf(t.source)),
  );
  const hasMarginInput =
    settlement !== "—" || hasAny(values, ["usedPhonePurchase", "otherSubsidy"]);
  const settlementBase = settlement === "—" ? 0 : settlement;
  const margin: number | "—" = hasMarginInput
    ? settlementBase + marginTerms.reduce((sum, t) => sum + t.signed, 0)
    : "—";

  return { settlementTerms, settlement, marginTerms, margin };
}

const computeDealerSettlement: RetailerFieldCompute = (values) =>
  computeSettlement(values).settlement;

const computeMargin: RetailerFieldCompute = (values) =>
  computeSettlement(values).margin;

export const RETAILER_FIELD_TABS: RetailerFieldTab[] = [
  {
    title: "개통 정보",
    groups: [
      {
        title: "일자",
        fields: [
          { key: "writtenAt", label: "작성일자", kind: "date" },
          { key: "receivedAt", label: "접수일", kind: "date" },
          { key: "receivedTime", label: "접수시간", kind: "time" },
          { key: "activatedAt", label: "개통일", kind: "date" },
          { key: "activatedTime", label: "개통시간", kind: "time" },
          // 요금제 변경 가능일 — 전용 위젯 ActivationDateCalc 이 담당하는 날짜 항목. 모달 배치표
          // (LAYOUT)에는 일반 입력칸으로 넣지 않고(위젯이 __activationDateCalc 셀로 그린다), 위젯
          // 안에서 "이대로 입력"·직접 선택으로 retailer_info.planChangeDate 에 "확정값"을 저장한다.
          // 서버 렌더/텍스트변환은 확정값이 있으면 그걸, 없으면 매장 식(tenants.planChangeExpr)+개통일
          // 계산값을 쓴다. 서식·텍스트 변환에서 {retailer_planChangeDate} / retailer.planChangeDate 로 노출.
          {
            key: "planChangeDate",
            label: "요금제 변경 가능일",
            kind: "date",
            hint: "이대로 입력(식+개통일 계산값) 또는 직접 선택으로 확정",
          },
        ],
      },
      {
        title: "개통 기본",
        fields: [
          {
            // 상품구분 — 모든 매장에 기본으로 있는 정식 항목. 기존 매장이 같은 이름의
            // 커스텀 항목을 이미 쓰면 field-registry 호환층이 그 옛 키·배치를 대신 쓴다.
            // 선택지는 매장 목록 + 라인업이 공유한 공통 목록을 합친 productCategory 마스터.
            key: "productCategory",
            label: "상품구분",
            kind: "select",
            optionsMaster: "productCategory",
            hint: "공통 상품구분과 우리 매장 상품구분을 함께 사용합니다.",
          },
          {
            key: "customerType",
            label: "고객유형",
            kind: "select",
            hint: "로드·약식·단순방문·가망고객 등 — 매장 마스터에서 추가·수정·삭제",
          },
          { key: "dealer", label: "개통대리점", kind: "select" },
          { key: "model", label: "단말기(모델명)", kind: "select" },
          {
            key: "assignedColor",
            label: "실제 개통 색상",
            kind: "text",
            hint: "비어 있으면 고객 색상 1순위로 자동 채움",
          },
          { key: "serial", label: "일련번호", kind: "text" },
          { key: "simNumber", label: "유심번호", kind: "text" },
          {
            key: "simType",
            label: "유심구매여부",
            kind: "radio",
            options: ["선납", "후납(다음달 합산)", "기존유심", "이심(가상유심)"],
          },
          {
            // 중고 개통·유심 단독·유선 등 — 신규 단말 카탈로그에 없는 개통 유형이라
            // 단말기(모델명) 칸에 "유심" 따위를 적으면 시세표·공시지원금 매칭이 깨진다.
            // 그래서 모델명 대신 이 항목으로 기록하고, 값이 선택되면 모델명 입력을 막는다.
            // 매장이 직접 항목을 추가·수정·삭제하는 단일 선택 드롭다운(매장 마스터).
            // (과거 고정 체크박스 2개 → 매장 관리형 단일 선택으로 전환)
            key: "usedSimActivation",
            label: "중고/유심단독 개통",
            kind: "select",
            hint: "선택하면 단말기(모델명)는 입력하지 않습니다(시세표·공시 매칭에서 제외).",
          },
          {
            key: "contractType",
            label: "약정유형",
            kind: "select",
            options: [
              "공시 / 24개월",
              "선약 / 12개월",
              "선약 / 24개월",
            ],
          },
          {
            key: "plan",
            label: "개통요금제",
            kind: "select",
            hint: "자동완성 + 직접 입력",
          },
        ],
      },
      {
        title: "지원금·부가",
        fields: [
          { key: "releasePrice", label: "출고가", kind: "number" },
          {
            key: PREORDER_BENEFIT_AMOUNT_KEY,
            label: "사전예약혜택금",
            kind: "number",
            hint: "금액을 적으면 출고가에서 자동으로 빠집니다",
          },
          { key: "publicSubsidy", label: "공시지원금", kind: "number" },
          { key: "extraSubsidy", label: "추가지원금", kind: "number" },
          {
            key: "remainingInstallment",
            label: "개통시 할부금액",
            kind: "calc",
            hint: "출고가−공시지원금−추가지원금−고객수납금(프리할부)",
            paletteHidden: true,
            compute: computeActivationInstallment,
          },
          {
            key: "addedAddons",
            label: "가입한 부가서비스",
            kind: "text",
            hint: "유지기간 자동계산",
            manage: true,
          },
          {
            key: "addonRetention",
            label: "부가서비스별 유지기간",
            kind: "text",
            paletteHidden: true,
          },
          { key: "skippedAddons", label: "미가입 부가서비스", kind: "text" },
          { key: "insurance", label: "보험", kind: "text" },
        ],
      },
      {
        title: "리베이트·차감",
        fields: [
          { key: "rebateBase", label: "액면 리베이트", kind: "number" },
          { key: "verbal1", label: "구두추가 1", kind: "number" },
          { key: "verbal2", label: "구두추가 2", kind: "number" },
          { key: "verbal3", label: "구두추가 3", kind: "number" },
          { key: "verbal4", label: "구두추가 4", kind: "number" },
          { key: "verbal5", label: "구두추가 5", kind: "number" },
          { key: "verbal6", label: "구두추가 6", kind: "number" },
          { key: "verbal7", label: "구두추가 7", kind: "number" },
          { key: "verbal8", label: "구두추가 8", kind: "number" },
          { key: "verbal9", label: "구두추가 9", kind: "number" },
          { key: "verbal10", label: "구두추가 10", kind: "number" },
          {
            key: "verbalNote",
            label: "구두 메모",
            kind: "textarea",
            manage: true,
            full: true,
          },
          {
            key: "rebateSum",
            label: "리베이트합계",
            kind: "calc",
            paletteHidden: true,
            compute: computeRebateSum,
          },
          {
            key: "deductionInput",
            label: "차감 합계 입력",
            kind: "number",
            hint: "차감 항목 합산 금액을 직접 입력",
          },
          {
            key: "deductionSum",
            label: "차감 합계",
            kind: "calc",
            paletteHidden: true,
            compute: computeDeductionSum,
          },
          {
            key: "deductionNote",
            label: "차감 메모",
            kind: "textarea",
            manage: true,
            full: true,
          },
          {
            key: "deductionTotal",
            label: "차감금액 합산",
            kind: "calc",
            paletteHidden: true,
            compute: computeDeductionTotal,
          },
          {
            key: "rebateActual",
            label: "실제 리베이트 총액",
            kind: "calc",
            paletteHidden: true,
            compute: computeRebateActual,
          },
        ],
      },
      {
        title: "정산",
        fields: [
          {
            key: "simPurchaseType",
            label: "유심 구매방식",
            kind: "select",
            options: [...SIM_PURCHASE_TYPES],
            // 매장이 관리할 카테고리가 아님 — 고정 옵션 유지 (manage: false)
          },
          {
            key: "simCustomerChargePrice",
            label: "고객청구 유심금액",
            kind: "number",
            readOnly: true,
          },
          {
            key: "simDealerSettlementPrice",
            label: "대리점정산 유심금액",
            kind: "number",
            readOnly: true,
          },
          {
            key: "mnpFee",
            label: "번이 수수료",
            kind: "number",
            hint: "출처 확정은 task 260에서. 일단 매장 직접 입력.",
          },
          {
            key: "freeInstallmentPayment",
            label: "고객수납금(프리할부)",
            kind: "number",
            hint: "직원 입력. 정산금 계산의 '수납' 항으로 차감된다.",
          },
          {
            key: "dealerSettlement",
            label: "대리점 정산금",
            kind: "calc",
            hint: "리베이트−추가지원금−고객수납금(프리할부)−번이 수수료+유심 정산반영",
            paletteHidden: true,
            compute: computeDealerSettlement,
          },
          { key: "otherSubsidy", label: "기타 지원금 합계", kind: "number" },
          {
            key: "otherSubsidyNote",
            label: "기타지원금 메모",
            kind: "textarea",
            manage: true,
            full: true,
          },
          { key: "usedPhonePurchase", label: "중고폰 수입금", kind: "number" },
          {
            key: "usedPhonePurchaseNote",
            label: "중고폰 수입금 메모",
            kind: "text",
            // 신청서 한 건의 메모라 매장 상수 팔레트에는 노출하지 않는다.
            paletteHidden: true,
          },
          { key: "margin", label: "마진", kind: "calc", paletteHidden: true, compute: computeMargin },
        ],
      },
      {
        title: "메모",
        fields: [
          {
            key: "activationMemo",
            label: "개통 관련 메모",
            kind: "textarea",
            full: true,
          },
          {
            key: "customerCareMemo",
            label: "고객 관리 메모",
            kind: "textarea",
            hint: "길게 작성 가능",
            full: true,
          },
        ],
      },
    ],
  },
  {
    title: "구매 정보",
    groups: [
      {
        title: "결제·구매",
        fields: [
          {
            // 수납 방법 — 매장별 마스터(paymentMethod)에서 검색·추가·수정·삭제.
            // 화면은 MasterCombobox 로 렌더(개통대리점과 동일). 기본 4종(현금·카드·
            // 계좌이체·기기변경 차감)은 신규 매장 가입 시 seed 로 주입되며, options 는
            // 두지 않는다(마스터가 SSOT).
            key: "paymentMethod",
            label: "수납 방법",
            kind: "select",
            manage: true,
          },
          { key: "actualPrice", label: "실구매가격", kind: "number" },
          { key: "installmentPrincipal", label: "할부원금", kind: "number" },
          { key: "extraAddonFee", label: "부가추가금합계", kind: "number" },
          {
            key: "installmentMonths",
            label: "할부개월",
            kind: "select",
            options: ["일시불", "3", "6", "9", "10", "12", "18", "24", "30", "36", "48"],
          },
        ],
      },
      {
        title: "복지할인",
        fields: [
          {
            // 복지할인 — 매장별 마스터(welfare)에서 검색·추가·수정·삭제.
            // 화면은 MasterCombobox 로 렌더(개통대리점과 동일). 기본 7종은 신규 매장
            // 가입 시 welfare-defaults seed 로 주입되며, options 는 두지 않는다(마스터가 SSOT).
            key: "welfareDiscount",
            label: "복지할인",
            kind: "select",
            manage: true,
          },
        ],
      },
      {
        title: "기타",
        fields: [
          {
            key: "microPayment",
            label: "소액결제",
            kind: "radio",
            options: ["차단", "미차단"],
          },
          {
            key: "bundle",
            label: "결합",
            kind: "checkbox",
            options: ["인터넷", "집전화", "TV/IPTV", "기타"],
          },
        ],
      },
    ],
  },
  {
    // 서식 선택 창의 [자동완성]이 계산해 저장하는 값. 일반 신청서 격자에는 같은 정보의
    // 원본 입력칸이 이미 있어 중복 노출하지 않고(applicationLayout EXCLUDED_R), 서식 슬롯과
    // 텍스트 자동변환에서는 신청서 한 건의 동적 값으로 쓸 수 있게 카탈로그·화이트리스트에 둔다.
    title: "자동완성",
    groups: [
      {
        title: "무선상담 계산값",
        fields: WIRELESS_AUTOFILL_FIELD_META.map(([key, label, kind]) => ({
          key,
          label,
          kind,
        })),
      },
    ],
  },
  {
    title: "추천·대리인",
    groups: [
      {
        title: "추천인",
        fields: [
          { key: "referrerName", label: "추천인 이름", kind: "text" },
          { key: "referrerId", label: "추천인 아이디", kind: "text" },
          { key: "referrerPhone", label: "추천인 번호", kind: "text", quickContact: true },
        ],
      },
      {
        // 법정대리인명·주민번호·휴대폰은 고객 입력 "법정대리인(미성년자)" 섹션과
        // 겹쳐 제거했다. 매장이 현장에서 찍어 올리는 신분증 사진만 남긴다.
        title: "법정대리인",
        fields: [
          { key: "guardianIdImage", label: "법정대리인 신분증 사진", kind: "file" },
        ],
      },
    ],
  },
];

const ALL_FIELDS = RETAILER_FIELD_TABS.flatMap((tab) =>
  tab.groups.flatMap((g) => g.fields),
);

/**
 * 저장되지 않는 빌트인 자동계산 항목을 원본 retailer_info로 한 번에 평가한다.
 *
 * 결과를 values에 다시 써 넣지 않는 것이 중요하다. 정산금·마진처럼 여러 계산값이
 * 같은 원본 숫자를 참조하므로, 앞에서 만든 표시값이 뒤 계산의 입력을 덮으면 안 된다.
 * 텍스트 자동변환과 서식 렌더가 이 별도 맵을 각자의 출력 경계에서 합친다.
 */
export function computeRetailerBuiltinCalcs(
  values: Record<string, unknown>,
): Record<string, number | "—"> {
  const out: Record<string, number | "—"> = {};
  for (const field of ALL_FIELDS) {
    if (field.kind === "calc" && field.compute) {
      out[field.key] = field.compute(values);
    }
  }
  return out;
}

/**
 * 팔레트("매장 상수")에 노출할 라벨 목록 — 표시 순서 보존.
 * 자동계산·내부 보조 필드(paletteHidden) 는 제외.
 */
export const RETAILER_PALETTE_LABELS: string[] = ALL_FIELDS.filter(
  (f) => !f.paletteHidden,
).map((f) => f.label);

/**
 * application_forms.retailer_info jsonb 에 저장 가능한 모든 키 집합.
 * 서버에서 PUT 페이로드 검증에 사용한다.
 */
export const RETAILER_FIELD_KEYS: ReadonlySet<string> = new Set(
  [
    ...ALL_FIELDS.map((f) => f.key),
    // 화면 항목은 아니지만 혜택금 취소·수정 때 할인 전 출고가를 되살리는 내부 저장값.
    PREORDER_BENEFIT_ORIGINAL_RELEASE_PRICE_KEY,
  ],
);

/**
 * 바코드 스캐너로 찍어 넣는 칸 — 입력값을 영문 대문자로 보정한다.
 * 스캐너는 키보드처럼 타건을 보내므로 한/영 키가 한글 상태면 영문 대신 한글이 조합돼
 * 들어온다("…396394ㄹ" 처럼). 이 두 칸만 한글→영문 역변환 + 대문자화를 적용하고,
 * 나머지 텍스트 칸(고객 요청사항 등 한글이 정상인 칸)은 건드리지 않는다.
 */
export const BARCODE_SCAN_FIELD_KEYS: ReadonlySet<string> = new Set([
  "serial", // 일련번호(단말기 IMEI)
  "simNumber", // 유심번호(유심 일련번호)
]);

/**
 * 판매점 입력 필드 key → 한글 라벨 맵.
 * 서식 작성 화면에서 "retailer.<key>" 형태의 칸 라벨을 사람이 읽을 수 있게
 * 표시하는 데 쓴다 (없으면 fieldKey 가 영문 그대로 노출됨).
 */
export const RETAILER_FIELD_LABELS: Readonly<Record<string, string>> =
  Object.fromEntries(ALL_FIELDS.map((f) => [f.key, f.label]));

/**
 * 약정유형(contractType)이 "선약"(선택약정) 계열인지 판별한다.
 * 옵션값은 "공시 / 24개월" · "선약 / 12개월" · "선약 / 24개월" 형태이며,
 * "선약"은 단말 공시지원금 대신 요금할인을 받는 유형이라 **공시지원금이 0**이어야 한다.
 * 클라(자동조회·표시·저장 직전)와 서버(저장 강제) 양쪽이 같은 판별을 쓰도록 여기 둔다.
 */
export function isPrepaidContract(contractType: unknown): boolean {
  return typeof contractType === "string" && contractType.startsWith("선약");
}
